import { EventPathType } from "../types";

import crematoriums from "./hwajang_detail.json";

/** 고이 대표번호. 화장장 번호가 없을 때 폴백한다. */
export const GOI_CALL_NUMBER = "1666-9784";

/**
 * 화장장 시설 상세 라우트 -> 그 화면의 이벤트 path.
 *
 * 문의 유형 모달은 "어느 화장장인지" 가 정해진 화면에서만 뜬다. 지역 목록
 * (crematorium-place-list/region/... 등)은 시군구 안 여러 시설을 나열하는 화면이라
 * 개장·이장, 고인 확인·방문이 걸 번호를 정할 수 없어서 대상이 아니다.
 *
 * 포털마다 라우트가 달라 한 곳에 모아 둔다. 라우트 문자열은 6개 포털을 통틀어 유일하다.
 * 하단 고정바(StepGuideFooter)가 전 포털 공용이라 여기서 라우트를 판정한다.
 */
export const CREMATORIUM_INQUIRY_EVENT_PATH: Record<string, EventPathType | undefined> = {
  // p_metropolitan
  "/veterans/hwajang/[companyname]": "veterans_crema",
  "/benefit/hwajang/[companyname]": "benef_crema",
  // p_dggb
  "/national-merit/crematorium/[companyname]": "nat_merit_crema",
  "/beneficiary/crematorium/[companyname]": "benef_crema",
  // p_gangwon
  "/gangwon-veterans/crema/[companyname]": "veterans_crema",
  "/gangwon-benefit/crema/[companyname]": "benef_crema",
  // p_honam
  "/free-national/free-crema/[companyname]": "fr_crema",
  "/free-benefit/free-crema/[companyname]": "fr_crema",
  // p_gyeongsang
  "/national/free-crema/[companyname]": "nat_fr-crema",
  "/support/free-crema/[companyname]": "fr-crema",
  // p_ch
  "/national-hero/free-crema/[companyname]": "nat_hr_fr_crema",
  "/benefit-support/free-crema/[companyname]": "benef_sup_fr_crema",
};

/**
 * 화장장 번호를 tel: 에 넣을 수 있는 형태로 정규화한다.
 *
 * hwajang_detail.json 은 e하늘 원본 형태라 `031-329-5900~2`(용인평온의 숲) 같은
 * 대표번호 범위 표기가 섞여 있다. 범위 표기는 앞 번호만 남긴다.
 */
export const normalizeCrematoriumPhone = (
  telephone: string | null | undefined
): string | null => {
  if (!telephone) return null;

  const head = telephone.split("~")[0].trim();
  return head || null;
};

/**
 * companyname 으로 화장장 번호를 찾는다. 12개 화장장 상세가 모두 [companyname] 라우트다.
 *
 * 라우트 파라미터에 포털마다 다른 장식이 붙는다. 접두도 있고 접미도 있다.
 * - metro, dggb, gangwon : `서울추모공원 (서초구)`            (그대로)
 * - gyeongsang           : `서울추모공원 (서초구) 소개`        (접미)
 * - ch                   : `... 국가유공자 안내`               (접미)
 * - honam                : `국가유공자 화장장 서울추모공원 ...` (접두)
 *
 * 장식 문자열을 목록으로 들고 있으면 포털이 문구를 바꿀 때마다 깨지므로,
 * 정확히 일치하는 이름을 먼저 찾고 없으면 파라미터에 포함된 이름 중 가장 긴 것을 고른다.
 * 가장 긴 것을 고르는 이유는 짧은 시설명이 긴 시설명 안에 들어있는 경우를 피하기 위해서다.
 *
 * @param companyname - router.query.companyname (string | string[] | undefined)
 * @returns 정규화된 번호, 못 찾거나 값이 없으면 null
 */
export const getCrematoriumPhoneByCompanyname = (
  companyname: string | string[] | undefined
): string | null => {
  if (typeof companyname !== "string") return null;

  const exact = crematoriums.find(
    (crematorium) => crematorium.companyname === companyname
  );
  if (exact) return normalizeCrematoriumPhone(exact.telephone);

  const contained = crematoriums
    .filter((crematorium) => companyname.includes(crematorium.companyname))
    .sort((a, b) => b.companyname.length - a.companyname.length)[0];

  return normalizeCrematoriumPhone(contained?.telephone);
};
